import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  BackgroundVariant,
  Panel,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Save, 
  Play, 
  AlertCircle,
  CheckCircle2,
  X,
  Undo,
  Redo,
  ZoomIn,
  ZoomOut,
  Maximize2,
  BookOpen,
  FlaskConical,
  PanelRightClose,
} from 'lucide-react';
import LLMStepNode from './nodes/LLMStepNode';
import AgentCallNode from './nodes/AgentCallNode';
import ToolCallNode from './nodes/ToolCallNode';
import HTTPStepNode from './nodes/HTTPStepNode';
import InputOutputNode from './nodes/InputOutputNode';
import StartNode from './nodes/StartNode';
import EndNode from './nodes/EndNode';
import ValidateStepNode from './nodes/ValidateStepNode';
import NotifyStepNode from './nodes/NotifyStepNode';
import TransformStepNode from './nodes/TransformStepNode';
import NodePalette from './NodePalette';
import NodeEditDialog from './NodeEditDialog';
import StepLibraryView from '../volcano/components/steps/StepLibraryView';
import { stepTemplateToNode } from '../volcano/utils/step-template-to-node';
import WorkflowTestRunner, { type StepStatus } from './WorkflowTestRunner';
import { cpFetch } from '@/lib/cpClient';
import { toast } from 'sonner';
import { persistDurableWorkflowChange } from '../durableSteps';

const EDGE_PULSE_MS = 2000;

interface AgentWorkflowDesignerProps {
  agent: any;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updatedWorkflow: any) => void;
}

const nodeTypes = {
  llmStep: LLMStepNode,
  agentCall: AgentCallNode,
  toolCall: ToolCallNode,
  httpStep: HTTPStepNode,
  validateStep: ValidateStepNode,
  notifyStep: NotifyStepNode,
  transformStep: TransformStepNode,
  inputOutput: InputOutputNode,
  start: StartNode,
  end: EndNode,
};

function FlowCanvas({ agent, onClose, onSave }: Omit<AgentWorkflowDesignerProps, 'isOpen'>) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { project } = useReactFlow();
  const [nodeIdCounter, setNodeIdCounter] = useState(0);
  const [editingNode, setEditingNode] = useState<{ id: string; type: string; data: any } | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [testRunnerOpen, setTestRunnerOpen] = useState(false);

  // Execution animation state (mirrors Dataflow Manager pattern)
  const [nodeExecutionStatus, setNodeExecutionStatus] = useState<Record<string, StepStatus>>({});
  const [edgePulse, setEdgePulse] = useState<Record<string, boolean>>({});

  // Handle SSE events from the test runner for node/edge animation
  const handleExecutionEvent = useCallback((event: any) => {
    switch (event.type) {
      case 'start':
        // Reset all node statuses to idle
        setNodeExecutionStatus({});
        setEdgePulse({});
        break;

      case 'step_start': {
        const nodeId = `node-${event.step_index}`;
        setNodeExecutionStatus(prev => ({ ...prev, [nodeId]: 'running' }));
        // Animate incoming edge
        setEdges(eds => eds.map(e => {
          if (e.target === nodeId) {
            return { ...e, animated: true, style: { ...e.style, stroke: '#2563eb', strokeWidth: 3 } };
          }
          return e;
        }));
        break;
      }

      case 'step_complete': {
        const nodeId = `node-${event.step_index}`;
        setNodeExecutionStatus(prev => ({ ...prev, [nodeId]: 'success' }));
        // Flash outgoing edge green, then reset
        setEdges(eds => eds.map(e => {
          if (e.source === nodeId) {
            return { ...e, animated: true, style: { ...e.style, stroke: '#16a34a', strokeWidth: 3 } };
          }
          if (e.target === nodeId) {
            return { ...e, animated: false, style: { ...e.style, stroke: '#16a34a', strokeWidth: 2 } };
          }
          return e;
        }));
        // Reset edge animation after pulse
        setTimeout(() => {
          setEdges(eds => eds.map(e => {
            if (e.source === nodeId) {
              return { ...e, animated: false, style: { ...e.style, stroke: '#16a34a', strokeWidth: 2 } };
            }
            return e;
          }));
        }, EDGE_PULSE_MS);
        break;
      }

      case 'step_error': {
        const nodeId = `node-${event.step_index}`;
        setNodeExecutionStatus(prev => ({ ...prev, [nodeId]: 'error' }));
        setEdges(eds => eds.map(e => {
          if (e.target === nodeId) {
            return { ...e, animated: false, style: { ...e.style, stroke: '#dc2626', strokeWidth: 2 } };
          }
          return e;
        }));
        break;
      }

      case 'step_skipped': {
        const nodeId = `node-${event.step_index}`;
        setNodeExecutionStatus(prev => ({ ...prev, [nodeId]: 'skipped' }));
        break;
      }

      case 'complete':
        // All edges back to default after a brief pause
        setTimeout(() => {
          setEdges(eds => eds.map(e => ({
            ...e,
            animated: false,
            style: { ...e.style, stroke: '#16a34a', strokeWidth: 2 },
          })));
        }, EDGE_PULSE_MS);
        break;

      case 'reset':
        setNodeExecutionStatus({});
        setEdges(eds => eds.map(e => ({
          ...e,
          animated: false,
          style: { ...e.style, stroke: '#3b82f6', strokeWidth: 2 },
        })));
        break;
    }
  }, [setEdges]);

  // Propagate execution status to node data
  useEffect(() => {
    const statusKeys = Object.keys(nodeExecutionStatus);
    if (statusKeys.length === 0) return;
    setNodes(nds => nds.map(n => ({
      ...n,
      data: {
        ...n.data,
        executionStatus: nodeExecutionStatus[n.id] || 'idle',
      }
    })));
  }, [nodeExecutionStatus, setNodes]);

  // Use ref to always have current nodes without causing re-renders
  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // Handle node editing - uses ref so it doesn't close over stale nodes
  const handleEditNode = useCallback((nodeId: string) => {
    console.log('🔧 handleEditNode called with nodeId:', nodeId);
    console.log('📋 Current nodes:', nodesRef.current.map(n => n.id));
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) {
      console.log('❌ Node not found:', nodeId);
      return;
    }

    console.log('✅ Opening edit dialog for node:', node);
    setEditingNode({
      id: nodeId,
      type: node.type || 'llmStep',
      data: node.data,
    });
  }, []); // No dependencies - always uses current nodesRef

  // Handle node deletion with confirmation - uses ref so it doesn't close over stale nodes
  const handleDeleteNode = useCallback((nodeId: string) => {
    console.log('🗑️ handleDeleteNode called with nodeId:', nodeId);
    console.log('📋 Current nodes:', nodesRef.current.map(n => n.id));
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) {
      console.log('❌ Node not found:', nodeId);
      return;
    }

    const nodeName = node.data?.stepName || node.data?.label || 'this node';
    console.log('⚠️ Showing confirmation for:', nodeName);
    const confirmed = window.confirm(
      `Are you sure you want to delete "${nodeName}"?\n\nThis will remove the node and all its connections.`
    );

    console.log('Confirmation result:', confirmed);
    if (confirmed) {
      console.log('✅ Deleting node:', nodeId);
      setNodes((nds) => nds.filter(n => n.id !== nodeId));
      setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    }
  }, [setNodes, setEdges]); // No nodes dependency - uses nodesRef

  // Removed problematic useEffect - callbacks are now attached when nodes are loaded or added

  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  
  // Load existing workflow (handle both single and multi-workflow agents)
  useEffect(() => {
    console.log('🔄 Workflow Designer: Loading workflow for agent:', {
      agentId: agent?.id || agent?.agent_id,
      agentName: agent?.name,
      hasVolcanoConfig: !!agent?.volcano_config,
      hasVolcanoWorkflow: !!agent?.volcano_workflow,
      workflowKeys: agent?.volcano_config?.workflows ? Object.keys(agent.volcano_config.workflows) : [],
      selectedWorkflow
    });

    if (!agent) {
      console.log('❌ No agent provided');
      setNodes([]);
      setEdges([]);
      setNodeIdCounter(0);
      setSelectedWorkflow(null);
      return;
    }

    // Check for multi-workflow structure (volcano_config.workflows)
    if (agent.volcano_config?.workflows) {
      const workflows = agent.volcano_config.workflows;
      const workflowNames = Object.keys(workflows);
      
      console.log('📋 Multi-workflow agent detected:', {
        workflowCount: workflowNames.length,
        workflowNames,
        selectedWorkflow
      });
      
      if (workflowNames.length === 0) {
        console.warn('⚠️ No workflows found in volcano_config.workflows');
        setNodes([]);
        setEdges([]);
        setNodeIdCounter(0);
        return;
      }
      
      // Auto-select first workflow if none selected
      const workflowToLoad = selectedWorkflow || workflowNames[0];
      
      if (!selectedWorkflow && workflowNames.length > 0) {
        console.log('✅ Auto-selecting first workflow:', workflowNames[0]);
        setSelectedWorkflow(workflowNames[0]);
      }
      
      // Load the workflow
      const workflow = workflows[workflowToLoad];
      console.log('📦 Loading workflow:', {
        workflowName: workflowToLoad,
        hasSteps: !!workflow?.steps,
        stepCount: workflow?.steps?.length || 0,
        steps: workflow?.steps?.map((s: any) => s.name) || []
      });
      
      if (workflow && workflow.steps && Array.isArray(workflow.steps) && workflow.steps.length > 0) {
        try {
          const { nodes: flowNodes, edges: flowEdges } = convertWorkflowToFlow(workflow);
          
          console.log('✅ Converted workflow to flow:', {
            nodeCount: flowNodes.length,
            edgeCount: flowEdges.length,
            nodeIds: flowNodes.map(n => n.id)
          });
          
          const nodesWithCallbacks = flowNodes.map((node) => ({
            ...node,
            data: {
              ...node.data,
              onEdit: () => handleEditNode(node.id),
              onDelete: () => handleDeleteNode(node.id),
            },
          }));
          
          setNodes(nodesWithCallbacks);
          setEdges(flowEdges);
          setNodeIdCounter(flowNodes.length);
          
          console.log('✅ Workflow loaded successfully');
        } catch (error) {
          console.error('❌ Failed to convert workflow to flow:', error);
          setNodes([]);
          setEdges([]);
          setNodeIdCounter(0);
        }
      } else {
        console.warn('⚠️ Workflow has no steps:', {
          workflowToLoad,
          workflow,
          hasWorkflow: !!workflow,
          hasSteps: !!workflow?.steps,
          isArray: Array.isArray(workflow?.steps),
          stepCount: workflow?.steps?.length || 0
        });
        setNodes([]);
        setEdges([]);
        setNodeIdCounter(0);
      }
    } 
    // Check for single workflow (volcano_workflow)
    else if (agent.volcano_workflow) {
      console.log('📋 Single workflow agent detected');
      if (agent.volcano_workflow.steps && Array.isArray(agent.volcano_workflow.steps) && agent.volcano_workflow.steps.length > 0) {
        try {
          const { nodes: flowNodes, edges: flowEdges } = convertWorkflowToFlow(agent.volcano_workflow);
          
          const nodesWithCallbacks = flowNodes.map((node) => ({
            ...node,
            data: {
              ...node.data,
              onEdit: () => handleEditNode(node.id),
              onDelete: () => handleDeleteNode(node.id),
            },
          }));
          
          setNodes(nodesWithCallbacks);
          setEdges(flowEdges);
          setNodeIdCounter(flowNodes.length);
        } catch (error) {
          console.error('❌ Failed to convert workflow to flow:', error);
          setNodes([]);
          setEdges([]);
          setNodeIdCounter(0);
        }
      } else {
        console.warn('⚠️ Single workflow has no steps');
        setNodes([]);
        setEdges([]);
        setNodeIdCounter(0);
      }
    } else {
      // No workflow found - clear nodes
      console.warn('⚠️ No workflow found in agent:', {
        agentId: agent.id || agent.agent_id,
        agentName: agent.name,
        hasVolcanoConfig: !!agent.volcano_config,
        hasVolcanoWorkflow: !!agent.volcano_workflow,
        volcanoConfigKeys: agent.volcano_config ? Object.keys(agent.volcano_config) : []
      });
      setNodes([]);
      setEdges([]);
      setNodeIdCounter(0);
    }
  }, [agent, selectedWorkflow, handleEditNode, handleDeleteNode]);

  // Convert Volcano workflow to ReactFlow format
  const convertWorkflowToFlow = (workflow: any) => {
    const flowNodes: Node[] = [];
    const flowEdges: Edge[] = [];
    let xOffset = 100;
    const yCenter = 300;
    const spacing = 400;

    workflow.steps?.forEach((step: any, index: number) => {
      const nodeId = `node-${index}`;
      
      // Determine node type based on step content
      let nodeType = 'llmStep';
      
      // Detect vision API step
      const isVisionStep = step.input?.messages && Array.isArray(step.input.messages);
      
      // Extract vision prompt if it's a vision step
      const getPromptText = () => {
        if (isVisionStep) {
          const content = step.input.messages[0]?.content;
          if (Array.isArray(content)) {
            const textContent = content.find((c: any) => c.type === 'text');
            return textContent?.text || 'Vision analysis step';
          }
        }
        return step.prompt || '';
      };
      
      let nodeData: any = {
        stepName: step.name || `Step ${index + 1}`,
        prompt: getPromptText(),
        // Default to Haiku 4.5 for text steps; OpenAI reserved for explicit vision steps.
        provider: step.provider || 'anthropic',
        model: step.model || 'claude-haiku-4-5-20251001',
        costEstimate: '<$0.001',
        isVisionStep: isVisionStep,
        input: step.input, // Pass input for edit dialog
        onEdit: () => handleEditNode(nodeId),
        onDelete: () => handleDeleteNode(nodeId),
      };

      // Agent step (Volcano provider: agent)
      if (step.provider === 'agent' || step.agent_id || step.agentId) {
        nodeType = 'agentCall';
        nodeData = {
          agentId: step.agent_id || step.agentId,
          agentName: step.agent_name || step.agentName || 'Unknown Agent',
          agentDescription: step.description,
          domain: step.domain,
          workflow: step.workflow,
          input: step.input,
          onEdit: () => handleEditNode(nodeId),
          onDelete: () => handleDeleteNode(nodeId),
        };
      }

      // MCP step (Volcano provider: mcp)
      if (step.provider === 'mcp' || step.mcp_tool || step.tool_name || step.toolName) {
        nodeType = 'toolCall';
        nodeData = {
          toolName: step.mcp_tool || step.tool_name || step.toolName,
          toolDescription: step.description,
          mcpServer: step.mcp_server || step.mcpServer,
          parameters: step.input || step.parameters,
          onEdit: () => handleEditNode(nodeId),
          onDelete: () => handleDeleteNode(nodeId),
        };
      }
      
      // Check if this is an HTTP step
      if (step.provider === 'http') {
        nodeType = 'httpStep';
        nodeData = {
          stepName: step.name || `Step ${index + 1}`,
          url: step.url,
          method: step.method || 'POST',
          description: step.description || `HTTP ${step.method || 'POST'} request`,
          continueOnError: step.continue_on_error,
          timeout: step.timeout,
          onEdit: () => handleEditNode(nodeId),
          onDelete: () => handleDeleteNode(nodeId),
        };
      }

      // Transform step
      if (step.provider === 'transform') {
        nodeType = 'transformStep';
        nodeData = {
          stepName: step.name || `Step ${index + 1}`,
          transform: step.transform,
          onEdit: () => handleEditNode(nodeId),
          onDelete: () => handleDeleteNode(nodeId),
        };
      }

      // Validate step (new provider)
      if (step.provider === 'validate') {
        nodeType = 'validateStep';
        nodeData = {
          stepName: step.name || `Step ${index + 1}`,
          schema: step.schema,
          on_error: step.on_error,
          input: step.input,
          onEdit: () => handleEditNode(nodeId),
          onDelete: () => handleDeleteNode(nodeId),
        };
      }

      // Notify step (new provider)
      if (step.provider === 'notify') {
        nodeType = 'notifyStep';
        nodeData = {
          stepName: step.name || `Step ${index + 1}`,
          channel: step.channel,
          url: step.url,
          payload: step.payload,
          onEdit: () => handleEditNode(nodeId),
          onDelete: () => handleDeleteNode(nodeId),
        };
      }

      if (step.condition) nodeData.condition = step.condition;

      flowNodes.push({
        id: nodeId,
        type: nodeType,
        position: { x: xOffset, y: yCenter },
        data: nodeData,
      });

      // Create edge to previous node
      if (index > 0) {
        flowEdges.push({
          id: `edge-${index - 1}-${index}`,
          source: `node-${index - 1}`,
          target: nodeId,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#3b82f6', strokeWidth: 2 },
        });
      }

      xOffset += spacing;
    });

    return { nodes: flowNodes, edges: flowEdges };
  };

  // Convert ReactFlow format back to Volcano workflow
  const convertFlowToWorkflow = () => {
    const steps: any[] = [];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    
    // Build adjacency list from edges
    const adjacencyList = new Map<string, string[]>();
    edges.forEach(edge => {
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, []);
      }
      adjacencyList.get(edge.source)!.push(edge.target);
    });

    // Find start nodes (nodes with no incoming edges)
    const incomingEdges = new Set(edges.map(e => e.target));
    const startNodes = nodes.filter(n => !incomingEdges.has(n.id));

    // Topological sort to get execution order
    const visited = new Set<string>();
    const sortedNodes: Node[] = [];

    const dfs = (nodeId: string) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      
      const neighbors = adjacencyList.get(nodeId) || [];
      neighbors.forEach(neighborId => dfs(neighborId));
      
      const node = nodeMap.get(nodeId);
      if (node) sortedNodes.unshift(node);
    };

    startNodes.forEach(node => dfs(node.id));

    // Convert nodes to workflow steps (skip non-step nodes like start/end/inputOutput)
    sortedNodes.forEach((node, index) => {
      const nodeType = String(node.type || '');
      const isExecutable =
        nodeType === 'llmStep' ||
        nodeType === 'httpStep' ||
        nodeType === 'toolCall' ||
        nodeType === 'agentCall' ||
        nodeType === 'transformStep' ||
        nodeType === 'validateStep' ||
        nodeType === 'notifyStep';
      if (!isExecutable) return;

      const step: any = {
        name: node.data.stepName || node.data.agentName || node.data.toolName || `step_${index + 1}`,
        step_number: index + 1,
        version: node.data.version || node.data.stepVersion || '1.0.0',
      };
      if (node.data.stepTemplateId) step.step_template_id = node.data.stepTemplateId;

      if (node.type === 'llmStep') {
        step.prompt = node.data.prompt;
        step.provider = node.data.provider;
        step.model = node.data.model;
        if (node.data.input) step.input = node.data.input;
      } else if (node.type === 'httpStep') {
        step.provider = 'http';
        step.url = node.data.url;
        step.method = node.data.method || 'POST';
        if (node.data.description) step.description = node.data.description;
        if (node.data.timeout) step.timeout = node.data.timeout;
        if (node.data.continueOnError !== undefined) step.continue_on_error = node.data.continueOnError;
      } else if (node.type === 'agentCall') {
        step.provider = 'agent';
        step.agent_id = node.data.agentId;
        step.agent_name = node.data.agentName;
        if (node.data.workflow) step.workflow = node.data.workflow;
        if (node.data.input) step.input = node.data.input;
      } else if (node.type === 'toolCall') {
        step.provider = 'mcp';
        step.mcp_server = node.data.mcpServer;
        step.mcp_tool = node.data.toolName;
        step.input = node.data.parameters || {};
      } else if (node.type === 'transformStep') {
        step.provider = 'transform';
        step.transform = node.data.transform || {};
      } else if (node.type === 'validateStep') {
        step.provider = 'validate';
        step.schema = node.data.schema || {};
        if (node.data.on_error) step.on_error = node.data.on_error;
        if (node.data.input) step.input = node.data.input;
      } else if (node.type === 'notifyStep') {
        step.provider = 'notify';
        step.channel = node.data.channel || 'webhook';
        step.url = node.data.url;
        step.payload = node.data.payload || {};
      }

      if (node.data.condition) step.condition = node.data.condition;

      steps.push(step);
    });

    return { steps };
  };

  // Handle new connections
  const onConnect = useCallback(
    (connection: Connection) => {
      const newEdge = {
        ...connection,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#3b82f6', strokeWidth: 2 },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  // Handle drag and drop from palette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;

      const { nodeType, nodeData } = JSON.parse(data);

      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNodeId = `node-${nodeIdCounter}`;
      setNodeIdCounter(prev => prev + 1);

      const newNode: Node = {
        id: newNodeId,
        type: nodeType,
        position,
        data: {
          ...nodeData,
          onEdit: () => handleEditNode(newNodeId),
          onDelete: () => handleDeleteNode(newNodeId),
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [project, nodeIdCounter, setNodes]
  );

  // Handle node edit save
  const handleSaveNodeEdit = (updatedData: any) => {
    if (!editingNode) return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === editingNode.id) {
          return {
            ...node,
            data: {
              ...updatedData,
              onEdit: () => handleEditNode(node.id),
              onDelete: () => handleDeleteNode(node.id),
            },
          };
        }
        return node;
      })
    );

    setEditingNode(null);
  };

  const addNodeFromStepTemplate = (template: any) => {
    const newNodeId = `node-${nodeIdCounter}`;
    setNodeIdCounter((prev) => prev + 1);
    const { nodeType, nodeData, templateId } = stepTemplateToNode(template);

    const newNode: Node = {
      id: newNodeId,
      type: nodeType || 'llmStep',
      position: { x: 100 + nodeIdCounter * 50, y: 300 },
      data: {
        ...nodeData,
        stepTemplateId: templateId,
        onEdit: () => handleEditNode(newNodeId),
        onDelete: () => handleDeleteNode(newNodeId),
      },
    };

    setNodes((nds) => nds.concat(newNode));
  };

  // Validate workflow
  const validateWorkflow = (): boolean => {
    const errors: string[] = [];

    // Check for disconnected nodes
    const connectedNodes = new Set<string>();
    edges.forEach(edge => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });

    const disconnectedNodes = nodes.filter(n => !connectedNodes.has(n.id) && nodes.length > 1);
    if (disconnectedNodes.length > 0) {
      errors.push(`${disconnectedNodes.length} disconnected node(s) found`);
    }

    // Check for cycles
    const hasCycle = detectCycle();
    if (hasCycle) {
      errors.push('Workflow contains a cycle');
    }

    // Check for empty workflow
    if (nodes.length === 0) {
      errors.push('Workflow is empty');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Detect cycles in the graph
  const detectCycle = (): boolean => {
    const adjacencyList = new Map<string, string[]>();
    edges.forEach(edge => {
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, []);
      }
      adjacencyList.get(edge.source)!.push(edge.target);
    });

    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const dfs = (nodeId: string): boolean => {
      visited.add(nodeId);
      recursionStack.add(nodeId);

      const neighbors = adjacencyList.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          if (dfs(neighbor)) return true;
        } else if (recursionStack.has(neighbor)) {
          return true;
        }
      }

      recursionStack.delete(nodeId);
      return false;
    };

    for (const node of nodes) {
      if (!visited.has(node.id)) {
        if (dfs(node.id)) return true;
      }
    }

    return false;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateWorkflow()) {
      toast.error('Workflow validation failed. Please fix the errors.');
      return;
    }

    setSaving(true);
    try {
      const updatedWorkflow = convertFlowToWorkflow();
      const persist = persistDurableWorkflowChange({
        proposed: updatedWorkflow,
        agent,
        workflowName: selectedWorkflow || agent?.volcano_config?.default_workflow || 'default',
      });
      if (!persist.ok) {
        toast.error(persist.message);
        setSaving(false);
        return;
      }

      // Extract agent dependencies
      const agentDependencies = nodes
        .filter(n => n.type === 'agentCall')
        .map(n => n.data.agentId);

      // Durable SoT is volcano_config.workflows — never a chat transcript or legacy volcano_workflow-only write.
      const response = await cpFetch(`/api/agents/${agent.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          volcano_config: persist.patch.volcano_config,
          depends_on: agentDependencies,
          create_version: true,
          version_notes: 'Updated durable workflow steps via Designer',
        }),
      });

      if (response.success) {
        toast.success('Workflow saved successfully!');
        onSave?.(updatedWorkflow);
        onClose();
      } else {
        throw new Error(response.error || 'Failed to save workflow');
      }
    } catch (error: any) {
      toast.error(`Failed to save workflow: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Get current workflow for test runner
  const getCurrentWorkflow = useCallback(() => {
    if (nodes.length === 0) return null;
    return convertFlowToWorkflow();
  }, [nodes, edges]);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Node Palette */}
      <NodePalette
        visibility={agent?.agent_type === 'internal' ? 'internal' : 'external'}
        onAddNode={(nodeType, nodeData) => {
        const newNodeId = `node-${nodeIdCounter}`;
        setNodeIdCounter(prev => prev + 1);
        const newNode: Node = {
          id: newNodeId,
          type: nodeType,
          position: { x: 100 + nodeIdCounter * 50, y: 300 },
          data: {
            ...nodeData,
            onEdit: () => handleEditNode(newNodeId),
            onDelete: () => handleDeleteNode(newNodeId),
          },
        };
        setNodes((nds) => nds.concat(newNode));
      }}
      />

      {/* Flow Canvas */}
      <div className="flex-1 relative h-full overflow-hidden" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
          <Controls />

          {/* Top Panel */}
          <Panel position="top-right" className="flex gap-2 mr-8">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setLibraryOpen(true)}
              className="bg-white"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Library
            </Button>
            <Button
              size="sm"
              variant={testRunnerOpen ? 'default' : 'outline'}
              onClick={() => setTestRunnerOpen(!testRunnerOpen)}
              className={testRunnerOpen ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-white'}
            >
              <FlaskConical className="w-4 h-4 mr-1" />
              Test
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Panel>

          {/* Validation Errors Panel */}
          {validationErrors.length > 0 && (
            <Panel position="bottom-center" className="bg-red-50 border border-red-200 rounded-md p-3 max-w-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm text-red-900 mb-1">Validation Errors:</div>
                  <ul className="text-xs text-red-700 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Panel>
          )}

          {/* Stats Panel */}
          <Panel position="top-left" className="bg-white border border-gray-200 rounded-md p-3">
            <div className="text-xs space-y-2">
              <div className="font-semibold text-gray-900">{agent.name}</div>
              
              {/* Multi-Workflow Selector */}
              {agent.volcano_config?.workflows && Object.keys(agent.volcano_config.workflows).length > 1 && (
                <div className="space-y-1">
                  <div className="text-gray-600 font-medium">Workflow:</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(agent.volcano_config.workflows).map((workflowName) => (
                      <Button
                        key={workflowName}
                        size="sm"
                        variant={selectedWorkflow === workflowName ? "default" : "outline"}
                        onClick={() => setSelectedWorkflow(workflowName)}
                        className="text-xs h-6 px-2"
                      >
                        {workflowName.replace('_', ' ')}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-gray-600">{nodes.length} nodes, {edges.length} connections</div>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Test Runner Panel (slide-out right) */}
      {testRunnerOpen && (
        <WorkflowTestRunner
          agent={agent}
          currentWorkflow={getCurrentWorkflow()}
          onExecutionEvent={handleExecutionEvent}
        />
      )}

      {/* Node Edit Dialog */}
      {editingNode && (
        <NodeEditDialog
          isOpen={true}
          onClose={() => setEditingNode(null)}
          nodeType={editingNode.type}
          nodeData={editingNode.data}
          onSave={handleSaveNodeEdit}
        />
      )}

      {/* Step Library Dialog */}
      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Step Library</DialogTitle>
            <DialogDescription>Select a reusable step template to add to the workflow.</DialogDescription>
          </DialogHeader>
          <div className="h-full overflow-hidden">
            <StepLibraryView
              visibility={agent?.agent_type === 'internal' ? 'internal' : 'external'}
              onInsert={(t) => {
                addNodeFromStepTemplate(t);
                setLibraryOpen(false);
                toast.success('Step inserted into workflow');
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AgentWorkflowDesigner(props: AgentWorkflowDesignerProps) {
  if (!props.isOpen) return null;
  
  return (
    <ReactFlowProvider>
      <Dialog open={props.isOpen} onOpenChange={props.onClose}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Workflow Designer</DialogTitle>
            <DialogDescription>Edit and test workflow steps for this agent.</DialogDescription>
          </DialogHeader>
          <FlowCanvas {...props} />
        </DialogContent>
      </Dialog>
    </ReactFlowProvider>
  );
}
